import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import Heading from "@/components/atoms/Heading";
import Text from "@/components/atoms/Text";
import CTA from "@/components/atoms/CTA";
import { SummaryStrip } from "../components/FormParts";
import DeviationProductsTable from "../components/DeviationProductsTable";
import DeviationServicesTable from "../components/DeviationServicesTable";
import CostSummary from "../components/CostSummary";
import { InspectionFormApi, InspectionSummary } from "../useInspectionForm";
import { useInspectionFormat } from "../useInspectionFormat";
import { DeviationPreview } from "../useDeviationPreview";
import { InspectionErrors } from "../validation";
import styles from "../styles.module.css";

interface DeviationStepProps {
  orderId: number;
  form: InspectionFormApi;
  summary: InspectionSummary;
  errors: InspectionErrors;
  preview: DeviationPreview;
}

/** Step 2, "Deviations": the deviation products and services on the original order, and what they cost. */
export default function DeviationStep({ orderId, form, summary, errors, preview }: DeviationStepProps) {
  const { t } = useTranslation();
  const format = useInspectionFormat();
  const { values } = form;

  if (!values.hasDeviation) {
    return (
      <div className={styles.stepBody}>
        <div className={styles.emptyCard} data-testid="inspection-no-deviation">
          <Heading order={3} styleOrder={5}>
            {t("inspections:deviations.emptyTitle")}
          </Heading>
          {summary.canRegisterDeviations ? (
            <>
              <Text size="sm" color="tertiary">
                {t("inspections:deviations.emptyBody")}
              </Text>
              <CTA
                intent="secondary"
                onClick={() => form.setHasDeviation(true)}
                data-testid="inspection-register-deviation"
              >
                {t("inspections:deviations.register")}
              </CTA>
            </>
          ) : (
            <Text size="sm" color="tertiary">
              {summary.isMarketOrder
                ? t("inspections:deviations.unavailableMarketOrder")
                : t("inspections:deviations.unavailable")}
            </Text>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.stepBody}>
      <SummaryStrip
        items={[
          { label: t("inspections:summary.voucherNumber"), value: `#${orderId}` },
          { label: t("inspections:summary.date"), value: dayjs().format("DD.MM.YYYY") },
          {
            label: t("inspections:summary.registrationNumber"),
            value: values.transportRegistrationNumber || "—",
          },
          { label: t("inspections:summary.totalWeight"), value: `${format.number(summary.totalWeightKg)} kg` },
        ]}
      />

      <div className={styles.fieldStack}>
        <Heading order={3} styleOrder={6} strong>
          {t("inspections:deviations.productsTitle")}
        </Heading>
        <DeviationProductsTable form={form} summary={summary} errors={errors} costs={preview.costs} />
      </div>

      <div className={styles.fieldStack}>
        <Heading order={3} styleOrder={6} strong>
          {t("inspections:deviations.servicesTitle")}
        </Heading>
        <DeviationServicesTable form={form} summary={summary} errors={errors} costs={preview.costs} />
      </div>

      {errors.hasDeviation && (
        <Text size="sm" color="error" data-testid="inspection-deviation-error">
          {t(errors.hasDeviation)}
        </Text>
      )}

      <CostSummary preview={preview} />
    </div>
  );
}
